/** UTC weekday indices matching JavaScript `Date#getUTCDay()` (0 = Sunday). */
export const MARKETPLACE_WEEKDAY_MIN = 0;
export const MARKETPLACE_WEEKDAY_MAX = 6;

/** Default partner schedule: Monday through Friday. */
export const DEFAULT_VENDOR_WORKING_WEEKDAYS: readonly number[] = [1, 2, 3, 4, 5];

const YMD_PATTERN = /^(\d{4})-(\d{2})-(\d{2})$/;

export function normalizeVendorWorkingWeekdays(
  value: readonly number[] | null | undefined,
): number[] {
  if (value == null || value.length === 0) {
    return [...DEFAULT_VENDOR_WORKING_WEEKDAYS];
  }
  const unique = [...new Set(value)].filter(
    (day) =>
      Number.isInteger(day) &&
      day >= MARKETPLACE_WEEKDAY_MIN &&
      day <= MARKETPLACE_WEEKDAY_MAX,
  );
  if (unique.length === 0) {
    return [...DEFAULT_VENDOR_WORKING_WEEKDAYS];
  }
  return unique.sort((a, b) => a - b);
}

export function isVendorWorkingWeekday(
  ymd: string,
  workingWeekdays: readonly number[],
): boolean {
  const weekday = utcWeekdayFromYmd(ymd);
  return weekday != null && workingWeekdays.includes(weekday);
}

export function utcWeekdayFromYmd(ymd: string): number | null {
  const match = YMD_PATTERN.exec(ymd);
  if (!match) return null;
  const date = new Date(
    `${match[1]}-${match[2]}-${match[3]}T00:00:00.000Z`,
  );
  if (Number.isNaN(date.getTime())) return null;
  return date.getUTCDay();
}

export function addUtcCalendarDaysYmd(ymd: string, days: number): string {
  const match = YMD_PATTERN.exec(ymd);
  if (!match) return ymd;
  const date = new Date(
    `${match[1]}-${match[2]}-${match[3]}T00:00:00.000Z`,
  );
  date.setUTCDate(date.getUTCDate() + days);
  return date.toISOString().slice(0, 10);
}

/**
 * Advance `slaDays` partner working days starting from `startYmd`, counting
 * `startYmd` when it is a working day (Thu + 3 → Mon for Mon–Fri).
 */
export function addInclusiveWorkingDaysYmd(
  startYmd: string,
  slaDays: number,
  workingWeekdays: readonly number[],
): string {
  const schedule = normalizeVendorWorkingWeekdays(workingWeekdays);
  if (slaDays <= 0) return startYmd;

  let count = 0;
  let cursor = startYmd;
  while (count < slaDays) {
    if (isVendorWorkingWeekday(cursor, schedule)) {
      count += 1;
      if (count >= slaDays) return cursor;
    }
    cursor = addUtcCalendarDaysYmd(cursor, 1);
  }
  return cursor;
}

/** Signed working-day distance from `fromYmd` to `toYmd` (like calendar daysBetween). */
export function workingDaysBetweenYmd(
  fromYmd: string,
  toYmd: string,
  workingWeekdays: readonly number[],
): number | null {
  if (fromYmd === toYmd) return 0;
  const schedule = normalizeVendorWorkingWeekdays(workingWeekdays);
  const forward = fromYmd < toYmd;
  let cursor = fromYmd;
  let count = 0;
  while (cursor !== toYmd) {
    cursor = addUtcCalendarDaysYmd(cursor, forward ? 1 : -1);
    if (isVendorWorkingWeekday(cursor, schedule)) {
      count += forward ? 1 : -1;
    }
  }
  return count;
}
